import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import SalovareSelectField from '../common/SalovareSelectField';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx } from '../common/formFieldStyles';
import { formatCalendarDate, parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';
import { DEFAULT_PHONE_COUNTRY_CODE, splitPhoneValue, toPhoneDto } from '../../utils/phoneUtils';

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email.trim());

const GENRE_VALUES = ['male', 'female'];

const normalizeGenreValue = (genre) => {
  if (!genre) {
    return '';
  }
  const key = genre.toString().toLowerCase();
  return GENRE_VALUES.includes(key) ? key : '';
};

const inputStyle = (hasError) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : '#e0e0e0'}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
});

const textareaStyle = (hasError) => ({
  ...inputStyle(hasError),
  minHeight: 96,
  resize: 'vertical',
  lineHeight: 1.45,
});

const StoreCustomerProfileDialog = ({
  open,
  onClose,
  customer,
  submitting,
  onSave,
  // Free stores are paid in store, so there is nothing to switch.
  showOnlinePayments = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [genre, setGenre] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [vatNumber, setVatNumber] = useState('');
  const [allergies, setAllergies] = useState('');
  const [medical, setMedical] = useState('');
  const [medications, setMedications] = useState('');
  const [pregnancy, setPregnancy] = useState('');
  const [onlinePaymentsAllowed, setOnlinePaymentsAllowed] = useState(true);
  const [errors, setErrors] = useState({});

  const accountHasEmail = Boolean(customer?.email?.trim());
  const accountHasPhone = Boolean(splitPhoneValue(customer?.phone, DEFAULT_PHONE_COUNTRY_CODE).number);
  const accountHasGenre = Boolean(normalizeGenreValue(customer?.genre));
  const accountHasBirthDate = Boolean(parseCalendarDateKey(customer?.birthDate));
  const accountHasVat = Boolean((customer?.vatNumber || customer?.VatNumber || '').trim());

  useEffect(() => {
    if (!open || !customer) {
      return;
    }

    const overlay = customer.overlay || {};
    const overlayPhone = overlay.displayPhone;
    const accountPhone = splitPhoneValue(customer.phone, DEFAULT_PHONE_COUNTRY_CODE);

    setDisplayName(overlay.displayName || customer.name || '');
    setEmail(overlay.displayEmail || customer.email || '');
    setPhoneCountryCode(overlayPhone?.countryCode || accountPhone.countryCode || DEFAULT_PHONE_COUNTRY_CODE);
    setPhoneNumber(overlayPhone?.number || accountPhone.number || '');
    setGenre(normalizeGenreValue(customer.genre));
    setBirthDate(parseCalendarDateKey(customer.birthDate));
    setVatNumber(customer.vatNumber || customer.VatNumber || '');
    setAllergies(overlay.allergiesAndSensitivities || '');
    setMedical(overlay.medicalConditions || '');
    setMedications(overlay.medications || '');
    setPregnancy(overlay.pregnancyNursingStatus || '');
    setOnlinePaymentsAllowed(!overlay.onlinePaymentsDisabled);
    setErrors({});
  }, [open, customer]);

  const handleSave = () => {
    const trimmedEmail = email.trim();
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setErrors({ email: 'customersManagement.details.editDialog.emailInvalid' });
      return;
    }

    setErrors({});
    const profile = {
      displayName: displayName.trim() || null,
      displayEmail: trimmedEmail || null,
      phone: toPhoneDto(phoneCountryCode, phoneNumber),
      allergiesAndSensitivities: allergies.trim() || null,
      medicalConditions: medical.trim() || null,
      medications: medications.trim() || null,
      pregnancyNursingStatus: pregnancy.trim() || null,
      onlinePaymentsDisabled: !onlinePaymentsAllowed,
    };

    if (!accountHasGenre) {
      profile.genre = genre || null;
    }
    if (!accountHasBirthDate) {
      profile.birthDate = toCalendarDateKey(birthDate);
    }
    if (!accountHasVat) {
      profile.vatNumber = vatNumber.trim() || null;
    }

    onSave(profile);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullScreen={isMobile}
      maxWidth="md"
      showCloseButton
      dividers
      paperBorderRadius={isMobile ? 0 : '17px'}
      title={t('customersManagement.details.editDialog.title')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={submitting}>
            {t('customersManagement.details.editDialog.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('customersManagement.details.editDialog.name')}
          </Typography>
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder={t('customersManagement.details.editDialog.namePlaceholder')}
            style={inputStyle(false)}
          />
          <StoreViewOnlyHint t={t} />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('customersManagement.details.editDialog.email')}
          </Typography>
          <input
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (errors.email) {
                setErrors({});
              }
            }}
            placeholder={t('customersManagement.details.editDialog.emailPlaceholder')}
            style={inputStyle(Boolean(errors.email))}
          />
          {errors.email ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {t(errors.email)}
            </Typography>
          ) : accountHasEmail ? (
            <StoreViewOnlyHint t={t} />
          ) : null}
        </Box>
        <SalovarePhoneInput
          label={t('customersManagement.details.editDialog.phone')}
          countryCode={phoneCountryCode}
          number={phoneNumber}
          onCountryCodeChange={setPhoneCountryCode}
          onNumberChange={setPhoneNumber}
          placeholder={t('customersManagement.details.editDialog.phonePlaceholder')}
          helperText={accountHasPhone ? t('customersManagement.details.editDialog.storeViewOnly') : undefined}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {accountHasGenre ? (
              <LockedProfileField
                label={t('customersManagement.table.genre')}
                value={t(`customersManagement.genre.${normalizeGenreValue(customer?.genre)}`)}
                hint={t('customersManagement.details.editDialog.customerProvided')}
              />
            ) : (
              <>
                <SalovareSelectField
                  label={t('customersManagement.table.genre')}
                  value={genre}
                  onChange={(event) => setGenre(event.target.value)}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                          {t('customersManagement.details.editDialog.selectGenre')}
                        </Typography>
                      );
                    }
                    return t(`customersManagement.genre.${value}`);
                  }}
                >
                  <MenuItem value="">
                    <em>{t('customersManagement.details.editDialog.selectGenre')}</em>
                  </MenuItem>
                  <MenuItem value="male">{t('customersManagement.genre.male')}</MenuItem>
                  <MenuItem value="female">{t('customersManagement.genre.female')}</MenuItem>
                </SalovareSelectField>
                <FieldHint t={t} locked={false} />
              </>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {accountHasBirthDate ? (
              <LockedProfileField
                label={t('customersManagement.table.birthDate')}
                value={customer?.birthDate
                  ? formatCalendarDate(customer.birthDate)
                  : t('common.notProvided')}
                hint={t('customersManagement.details.editDialog.customerProvided')}
              />
            ) : (
              <>
                <Typography variant="subtitle2" sx={fieldLabelSx}>
                  {t('customersManagement.table.birthDate')}
                </Typography>
                <SalovareDatePicker
                  value={birthDate}
                  onChange={setBirthDate}
                  disableFuture
                  maxDate={new Date()}
                  openTo="year"
                />
                <FieldHint t={t} locked={false} />
              </>
            )}
          </Box>
        </Box>
        <Box>
          {accountHasVat ? (
            <LockedProfileField
              label={t('vat.fields.number')}
              value={customer?.vatNumber || customer?.VatNumber}
              hint={t('customersManagement.details.editDialog.customerProvided')}
            />
          ) : (
            <>
              <Typography variant="subtitle2" sx={fieldLabelSx}>{t('vat.fields.number')}</Typography>
              <input value={vatNumber} onChange={(event) => setVatNumber(event.target.value)} style={inputStyle(false)} />
              <FieldHint t={t} locked={false} />
            </>
          )}
        </Box>
        {showOnlinePayments ? (
          <Box>
            <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
              {t('customersManagement.details.onlinePayments.title')}
            </Typography>
            <SwitchField
              checked={onlinePaymentsAllowed}
              onChange={(event) => setOnlinePaymentsAllowed(event.target.checked)}
              label={t('customersManagement.details.onlinePayments.label')}
              helpText={t('customersManagement.details.onlinePayments.help')}
            />
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 0 }}>
              {t('customersManagement.details.consultation.title')}
            </Typography>
            <StoreViewOnlyHint t={t} sx={{ mt: 0.5 }} />
          </Box>
          <ConsultationField
            label={t('customersManagement.details.consultation.allergies')}
            helper={t('customersManagement.details.editDialog.allergiesHelper')}
            value={allergies}
            onChange={setAllergies}
          />
          <ConsultationField
            label={t('customersManagement.details.consultation.medical')}
            helper={t('customersManagement.details.editDialog.medicalHelper')}
            value={medical}
            onChange={setMedical}
          />
          <ConsultationField
            label={t('customersManagement.details.consultation.medications')}
            helper={t('customersManagement.details.editDialog.medicationsHelper')}
            value={medications}
            onChange={setMedications}
          />
          <ConsultationField
            label={t('customersManagement.details.consultation.pregnancy')}
            helper={t('customersManagement.details.editDialog.pregnancyHelper')}
            value={pregnancy}
            onChange={setPregnancy}
          />
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

const LockedProfileField = ({ label, value, hint }) => (
  <Box>
    <Typography variant="subtitle2" sx={fieldLabelSx}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, minHeight: 48, display: 'flex', alignItems: 'center' }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
      {hint}
    </Typography>
  </Box>
);

const StoreViewOnlyHint = ({ t, sx }) => (
  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', ...sx }}>
    {t('customersManagement.details.editDialog.storeViewOnly')}
  </Typography>
);

const FieldHint = ({ t, locked }) => (
  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
    {t(locked
      ? 'customersManagement.details.editDialog.customerProvided'
      : 'customersManagement.details.editDialog.accountProfile')}
  </Typography>
);

const ConsultationField = ({ label, helper, value, onChange }) => (
  <Box>
    <Typography variant="subtitle2" sx={fieldLabelSx}>
      {label}
    </Typography>
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={textareaStyle(false)}
    />
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
      {helper}
    </Typography>
  </Box>
);

export default StoreCustomerProfileDialog;
